import { faArrowDown, faArrowUp, faCog, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    safePolygon,
    shift,
    useFloating,
    useHover,
    useInteractions,
    type Placement,
} from '@floating-ui/react';
import { useState } from 'react';
import type React from 'react';
import { useAddElementSlotMenuIcon } from '../addElementSlot/hooks/useAddElementSlotMenuIcon';
import classNames from '../../../../utils/classNames';
import type {
    EditorRenderRuntime,
    ElementLocation,
    RenderContext,
    StructurePathActionHelper,
} from './types';

interface EditorFrameProps {
    readonly elementType: string;
    readonly runtime: EditorRenderRuntime;
    readonly location: ElementLocation;
    readonly renderContext: RenderContext;
    readonly containerClassName: string;
    readonly canEditSettings: boolean;
    readonly children?: React.ReactNode;
}

const TOOLBAR_BUTTON_CLASS =
    'flex h-6 w-6 items-center justify-center rounded bg-white/85 text-slate-700 shadow-sm transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40';
const TOOLBAR_OFFSET = 8;
const TOOLBAR_FLOATING_PADDING = 8;

function isInsideRowsOrColumnsLane(location: ElementLocation): boolean {
    const { listPath } = location;

    for (let index = 0; index < listPath.length; index += 1) {
        const segment = listPath[index];

        if (segment === 'columns') {
            const maybeColumnIndex = listPath[index + 1];
            if (typeof maybeColumnIndex === 'number' && index + 1 === listPath.length - 1) {
                return true;
            }
        }

        if (segment === 'rows') {
            const maybeRowIndex = listPath[index + 1];
            const maybeChildren = listPath[index + 2];
            if (
                typeof maybeRowIndex === 'number' &&
                maybeChildren === 'children' &&
                index + 2 === listPath.length - 1
            ) {
                return true;
            }
        }
    }

    return false;
}

function getToolbarPlacementForElement(elementType: string, location: ElementLocation): Placement {
    if (elementType === 'columns' || elementType === 'rows') {
        return 'left-start';
    }

    if (isInsideRowsOrColumnsLane(location)) {
        return 'right-start';
    }

    return 'left-start';
}

interface ToolbarAction {
    key: string;
    label: string;
    icon: typeof faArrowUp;
    disabled: boolean;
    onClick: () => void;
    className?: string;
}

function getToolbarActions(
    actionHelper: StructurePathActionHelper,
    structureController: EditorRenderRuntime['editor']['structureController'],
    elementType: string,
    location: ElementLocation,
    renderContext: RenderContext,
    canEditSettings: boolean
): ToolbarAction[] {
    const actions: ToolbarAction[] = [
        {
            key: 'move-up',
            label: `Move ${elementType} up`,
            icon: faArrowUp,
            disabled: !actionHelper.canMoveUp,
            onClick: () => actionHelper.moveUp(),
        },
        {
            key: 'move-down',
            label: `Move ${elementType} down`,
            icon: faArrowDown,
            disabled: !actionHelper.canMoveDown,
            onClick: () => actionHelper.moveDown(),
        },
        ...(canEditSettings
            ? [
                  {
                      key: 'settings',
                      label: `Edit ${elementType} settings`,
                      icon: faCog,
                      disabled: false,
                      onClick: () =>
                          structureController.requestSettingsEdit({
                              location,
                              context: renderContext,
                          }),
                  },
              ]
            : []),
        {
            key: 'delete',
            label: `Delete ${elementType}`,
            icon: faTrash,
            disabled: false,
            onClick: () => actionHelper.remove(),
            className: 'text-red-600 hover:text-red-700',
        },
    ];

    return actions;
}

export function EditorFrame({
    elementType,
    runtime,
    location,
    renderContext,
    containerClassName,
    canEditSettings,
    children,
}: EditorFrameProps): JSX.Element {
    const [isToolbarOpen, setIsToolbarOpen] = useState(false);
    const { structureController } = runtime.editor;
    const getMenuIconForElementType = useAddElementSlotMenuIcon();
    const toolbarPlacement = getToolbarPlacementForElement(elementType, location);
    const { refs, floatingStyles, context } = useFloating({
        open: isToolbarOpen,
        onOpenChange: setIsToolbarOpen,
        placement: toolbarPlacement,
        strategy: 'fixed',
        middleware: [
            offset(TOOLBAR_OFFSET),
            flip({ padding: TOOLBAR_FLOATING_PADDING }),
            shift({ padding: TOOLBAR_FLOATING_PADDING }),
        ],
        whileElementsMounted: autoUpdate,
    });
    const hover = useHover(context, {
        // Keep hover active while the pointer moves from element content to the
        // floating toolbar, so controls remain clickable.
        handleClose: safePolygon({
            blockPointerEvents: true,
        }),
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

    const actionHelper = structureController.getHelperAtPath(location);
    const toolbarActions = getToolbarActions(
        actionHelper,
        structureController,
        elementType,
        location,
        renderContext,
        canEditSettings
    );
    const toolbarElementIcon = getMenuIconForElementType(elementType);
    const shouldShowHoverOutline = isToolbarOpen && elementType !== 'page';

    return (
        <div
            data-rb-editor-frame="true"
            ref={refs.setReference}
            className={classNames(containerClassName, 'relative')}
            {...getReferenceProps()}
        >
            {shouldShowHoverOutline ? (
                <div
                    aria-hidden={true}
                    className="pointer-events-none absolute inset-0 z-10 rounded-sm border border-dashed border-slate-400/80"
                />
            ) : null}
            {children}
            {isToolbarOpen ? (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        data-rb-editor-toolbar="true"
                        className="z-20 flex flex-col items-center gap-1 rounded border border-slate-300 bg-white/95 p-1 shadow-lg backdrop-blur-sm"
                        {...getFloatingProps()}
                    >
                        <span
                            className="pointer-events-none flex h-6 w-6 items-center justify-center text-slate-400"
                            aria-label={`${elementType} element toolbar`}
                        >
                            <FontAwesomeIcon icon={toolbarElementIcon} className="h-3 w-3" />
                        </span>
                        {toolbarActions.map((action) => (
                            <button
                                key={action.key}
                                type="button"
                                aria-label={action.label}
                                className={classNames(TOOLBAR_BUTTON_CLASS, action.className)}
                                onClick={() => {
                                    setIsToolbarOpen(false);
                                    action.onClick();
                                }}
                                disabled={action.disabled}
                            >
                                <FontAwesomeIcon icon={action.icon} className="h-3 w-3" />
                            </button>
                        ))}
                    </div>
                </FloatingPortal>
            ) : null}
        </div>
    );
}
